import React, { FC, ReactNode, useEffect, useState } from "react";
import axiosInstant from "../../../../../api";
import { requestProductPdpUrlScore, rescoreProductPdpUrl } from "../../../../../api/endpoints";
import { useUser } from "../../../../../hooks/useUser";
import { IconAlertGlyph, IconClockGlyph } from "../panels";
import { ScoreState, PrimaryButton, OutlineButton, ProgressSweep, MonoError } from "./ScoreState";
import { PdpUrlRow, humanReason } from "./pageScore";

/**
 * Action A — "Score now" / "Run report", on the Not-scored state and its
 * stalled variant (handoff §06 A, §10.8).
 *
 * Two paths — the same split TDC-1499 made on the product settings panel
 * (ProductPdpUrlsPanel), so a page is scored the same way from either surface:
 *
 *   · INTERNAL roles call the synchronous rescore (`proTeamControl` on the API).
 *     A 30–90 SECOND BLOCKING TASK, not a click: it fetches the live page and
 *     runs one audit, and the response IS the score — so the duration is stated
 *     before the press, an indeterminate sweep and an elapsed counter run while
 *     it works (never a percentage: the request emits no progress events, and a
 *     fake bar that stalls at 80% is worse than none), and a leave-warning is
 *     real because navigating away loses the result.
 *
 *   · EVERYONE ELSE (brand admin / member / sales) calls the async
 *     `request-score`: the API enqueues a forced scoring job and writes a
 *     pending attempt, the silent refresh brings that attempt back, and the
 *     panel's poll (usePendingScorePoll) picks the score up when it lands.
 *     Rate-limited per user on the API (429); one run at a time per row (409).
 *
 * The sync role set is the API's `proTeamControl` list, verbatim; every
 * other role takes the async path with the brand roles.
 */

const SYNC_SCORE_ROLES = new Set(["ADMIN", "PRO_TEAM_ADMIN", "ACCOUNT_MANAGER_ADMIN", "POST_PRODUCTION_ADMIN", "SALES_TEAM"]);

/** Roles that may call the synchronous rescore endpoint. */
export const canScoreSync = (role: string | null | undefined): boolean => !!role && SYNC_SCORE_ROLES.has(String(role));

const SYNC_HELPER = "Fetches the live page, then runs one audit. Takes 30–90 seconds.";
const ASYNC_HELPER = "Runs in the background — usually a few minutes. The score appears here when it finishes.";

interface Props {
  row: PdpUrlRow;
  /** The row's own product — a video can reach several. */
  productId: number | null;
  compact?: boolean;
  /** Pending for over 15 minutes: the stalled copy, with Check again. */
  stalled?: boolean;
  startedMinutesAgo?: number;
  /** The "1 of 3 pages" line, rendered here too — see pageScore.rankRows. */
  pageCount?: ReactNode;
  onCheckAgain: () => void;
  /** Silent refresh after the request lands; the panel then swaps state and this unmounts. */
  onScored: () => Promise<void>;
  /** Lets the panel show the "Scoring" header chip. */
  onScoringChange?: (scoring: boolean) => void;
}

export const NotScoredYet: FC<Props> = ({
  row,
  productId,
  compact = false,
  stalled = false,
  startedMinutesAgo = 0,
  pageCount,
  onCheckAgain,
  onScored,
  onScoringChange,
}) => {
  const { role } = useUser();
  const sync = canScoreSync(role);
  const allowed = !!productId && !!row?.id;
  const [phase, setPhase] = useState<"idle" | "scoring" | "failed">("idle");
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const scoring = phase === "scoring";

  useEffect(() => {
    onScoringChange?.(scoring);
  }, [scoring, onScoringChange]);

  // Sync only. The elapsed counter is the honest signal that something is
  // happening, and the reader checks it against the stated 30–90 seconds.
  useEffect(() => {
    if (!scoring || !sync) return undefined;
    setElapsed(0);
    const timer = window.setInterval(() => setElapsed((n) => n + 1), 1000);
    return () => window.clearInterval(timer);
  }, [scoring, sync]);

  // Sync only. The response is the score. Leaving the page loses it.
  useEffect(() => {
    if (!scoring || !sync) return undefined;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [scoring, sync]);

  const fail = (e: any) => {
    const raw = e?.response?.data?.error ?? e?.response?.data ?? e?.message;
    setError(typeof raw === "string" && raw ? raw : "Unknown error");
    setPhase("failed");
  };

  const runSync = async () => {
    try {
      await axiosInstant.post(rescoreProductPdpUrl(productId as number, row.id));
      // The panel swaps to Scored once the refresh lands and this unmounts; if
      // the refresh itself fails we fall back to idle rather than hang.
      await onScored();
      setPhase("idle");
    } catch (e: any) {
      fail(e);
    }
  };

  const runAsync = async () => {
    try {
      await axiosInstant.post(requestProductPdpUrlScore(productId as number, row.id));
      // The refresh brings back the pending attempt; the panel then renders
      // "Scoring in progress" and polls until the score lands.
      await onScored();
      setPhase("idle");
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 409) {
        // Already running — not a failure. Refresh so the pending state shows.
        await onScored();
        setPhase("idle");
        return;
      }
      if (status === 429) {
        setError("RateLimited");
        setPhase("failed");
        return;
      }
      fail(e);
    }
  };

  const run = async () => {
    if (!allowed) return;
    setPhase("scoring");
    setError(null);
    if (sync) await runSync();
    else await runAsync();
  };

  const primaryLabel = sync ? "Score now" : "Run report";
  const helper = sync ? SYNC_HELPER : ASYNC_HELPER;

  const scoreNow = (label = primaryLabel) => (
    <PrimaryButton onClick={run} full={compact}>
      {label}
    </PrimaryButton>
  );

  // ---- A2 · in progress ----
  if (scoring) {
    if (!sync) {
      // The async request itself is near-instant; this state lasts until the
      // silent refresh brings back the pending attempt.
      return (
        <ScoreState
          skin="working"
          icon={<IconClockGlyph size={20} strokeWidth={1.7} />}
          title="Starting the report"
          body="Queuing the audit. This page will show its progress in a moment."
          action={
            <PrimaryButton disabled full={compact}>
              Starting…
            </PrimaryButton>
          }
          compact={compact}
        />
      );
    }
    // Sync: blocking, 30–90s.
    return (
      <ScoreState
        skin="working"
        icon={<IconClockGlyph size={20} strokeWidth={1.7} />}
        title="Scoring this page"
        body="Fetching the live page, then running the audit. This usually takes 30–90 seconds."
        action={
          <PrimaryButton disabled full={compact}>
            Scoring…
          </PrimaryButton>
        }
        footer={
          <div className="flex flex-col items-center gap-2">
            <ProgressSweep />
            <span className="text-text-2 tabular-nums" style={{ fontSize: 11.5 }}>
              {elapsed} seconds elapsed
            </span>
            <span className="text-down" style={{ fontSize: 11 }}>
              Stay on this page — leaving cancels the request.
            </span>
          </div>
        }
        compact={compact}
      />
    );
  }

  // ---- A3 / A4 · failed: a known token gets human copy, anything else the mono block ----
  if (phase === "failed") {
    const reason = humanReason(error);
    return (
      <ScoreState
        skin="error"
        icon={<IconAlertGlyph size={20} strokeWidth={1.7} />}
        title={reason.known ? reason.text : "Scoring failed"}
        body="Nothing was saved. The page is still registered and will be scored at its next scheduled run."
        action={
          <>
            {scoreNow("Try again")}
            <OutlineButton onClick={() => setPhase("idle")} full={compact}>
              Dismiss
            </OutlineButton>
          </>
        }
        footer={
          reason.known ? (
            // The raw token still prints small so support can quote it.
            <div className="text-center text-muted" style={{ fontSize: 10.5 }}>
              {error}
            </div>
          ) : (
            <MonoError text={error || "Unknown error"} />
          )
        }
        compact={compact}
      />
    );
  }

  // ---- stalled · state 4 wearing different copy; amber, nothing has failed ----
  if (stalled) {
    return (
      <ScoreState
        skin="empty"
        icon={<IconClockGlyph size={20} strokeWidth={1.7} />}
        title="Scoring is taking longer than expected"
        body={`Started ${startedMinutesAgo} minutes ago. It usually takes a few minutes; this will resolve on its own or be retried at the next scheduled run.`}
        action={
          <>
            <OutlineButton onClick={onCheckAgain} full={compact}>
              Check again
            </OutlineButton>
            {allowed && scoreNow()}
          </>
        }
        helper={allowed ? helper : undefined}
        compact={compact}
      />
    );
  }

  // ---- A1 / state 4 · not scored yet ----
  return (
    <ScoreState
      skin="empty"
      icon={<IconClockGlyph size={20} strokeWidth={1.7} />}
      title="Not scored yet"
      body="The first score runs within minutes of a page being added, then on a regular schedule."
      action={allowed ? scoreNow() : undefined}
      helper={allowed ? helper : undefined}
      footer={pageCount ? <div className="text-center">{pageCount}</div> : undefined}
      compact={compact}
    />
  );
};

export default NotScoredYet;
